import { Check } from 'lucide-react';

const pricingTiers = [
  {
    name: 'Starter',
    price: 'Free preview',
    description: 'For testing and exploring features',
    features: [
      'Up to 3 team cards',
      'Basic spending controls',
      'Receipt capture',
      'Activity feed',
      'CSV export',
      'Email support'
    ],
    cta: 'Join waitlist',
    highlighted: false
  },
  {
    name: 'Pro',
    price: 'Coming soon',
    description: 'Full control for growing teams',
    features: [
      'Unlimited team cards',
      'Advanced spending rules',
      'Merchant/category controls',
      'Vendor transfers',
      'Custom budget categories',
      'Priority support'
    ],
    cta: 'Join waitlist',
    highlighted: true
  }
];

const Pricing = ({ onWaitlistClick }) => {
  return (
    <section id="pricing" className="py-24 md:py-32 relative">
      <div className="container mx-auto">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold heading-font text-white">
            Simple, transparent pricing
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Start for free. Scale as you grow. No hidden fees.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              className={`relative p-8 rounded-2xl border backdrop-blur-sm transition-all ${
                tier.highlighted
                  ? 'bg-white/10 border-indigo-600/50 shadow-lg shadow-indigo-600/10'
                  : 'bg-white/5 border-white/10 hover:border-white/20'
              }`}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-indigo-600 text-white text-sm font-medium rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-white heading-font mb-2">
                    {tier.name}
                  </h3>
                  <p className="text-gray-400 text-sm">{tier.description}</p>
                </div>

                <div>
                  <div className="text-4xl font-bold text-white heading-font">
                    {tier.price}
                  </div>
                </div>

                <ul className="space-y-3">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-indigo-600/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={14} className="text-indigo-400" />
                      </div>
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onWaitlistClick}
                  className={`w-full py-3 rounded-xl font-medium transition-colors ${
                    tier.highlighted
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                  }`}
                >
                  {tier.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* How pricing works */}
        <div className="max-w-3xl mx-auto mt-16 mb-8">
          <div className="bg-white/5 rounded-xl border border-white/10 p-8 backdrop-blur-sm">
            <h3 className="text-2xl font-bold heading-font text-white mb-4">
              How pricing works
            </h3>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p>
                Limitly is a subscription Software as a Service for businesses. You pay a monthly or annual fee for access to our spend management platform, team card controls, and reporting tools.
              </p>
              <p className="text-sm text-gray-400">
                We may also earn interchange revenue from card spend via our financial institution partners. This allows us to keep subscription pricing competitive while delivering enterprise-grade features to small businesses.
              </p>
            </div>
          </div>
        </div>

        {/* Contact sales */}
        <div className="text-center mt-8">
          <p className="text-gray-400 text-sm mb-3">Need a plan for larger teams?</p>
          <a
            href="/contact"
            className="inline-block text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
          >
            Contact sales →
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
